// Shared calculation helpers used across multiple pages, ported verbatim from the
// original app (HOME / FOOD / WORKOUT sections).
import { S } from '../state.js';
import { ING_BASE } from '../data/ingredients.js';
import { n0, n1 } from './format.js';
import { shiftDate, fmtDateShort, today } from './dates.js';
import { cicon } from './icons.js';

export function ingList() { return S.customIngs || ING_BASE; }
export function dishList() { return S.customDishes || []; }

export function dayKbzhu(date) {
  const fl = S.foodLog[date] || {};
  let k = 0, p = 0, f = 0, c = 0;
  ['breakfast', 'lunch', 'dinner', 'snacks'].forEach(m => {
    (fl[m] || []).forEach(it => {
      // "eaten" is just a completion checkbox for reminders — everything logged (planned
      // or already eaten) counts toward the day's totals, so you can plan ahead.
      k += it.k || 0; p += it.p || 0; f += it.f || 0; c += it.c || 0;
    });
  });
  return { k: n0(k), p: n1(p), f: n1(f), c: n1(c) };
}

export function kbzhuBar(k, p, f, c) {
  const g = S.settings;
  const kcalOver = k > g.goalKcal;

  function renderRow(label, cls, pct, fillColor, text) {
    const fill = cls === 'over' ? 'pf-red' : cls === 'warn' ? 'pf-amber' : fillColor;
    return `<div class="prog-row">
      <div class="prog-header">
        <span class="prog-label">${label}</span>
        <span class="prog-value ${cls}">${text}</span>
      </div>
      <div class="ptrack ${cls === 'over' ? 'over' : cls === 'warn' ? 'warn' : ''}"><div class="pfill ${fill}" style="width:${pct}%"></div></div>
    </div>`;
  }

  const kCls = k > g.goalKcal ? 'over' : (k / g.goalKcal > 0.85 ? 'warn' : '');
  const kRow = renderRow('Калории', kCls, Math.min((k / g.goalKcal) * 100, 100), 'pf-amber', `${k}/${g.goalKcal} ккал`);

  // Protein is a MINIMUM, not a ceiling — no "over" state exists for it at all.
  const pReached = p >= g.goalP;
  const pCls = pReached ? 'good' : '';
  const pRow = renderRow('Белки (мин.)', pCls, Math.min((p / g.goalP) * 100, 100), 'pf-blue', `${p}/${g.goalP}+ г${pReached ? ' ✓' : ''}`);

  function softRow(label, val, goal, color, unit) {
    const overFrac = (val - goal) / goal;
    let cls = '';
    if (overFrac > 0.15 && kcalOver) cls = 'over';
    else if (overFrac > 0.3) cls = 'warn';
    return renderRow(label, cls, Math.min((val / goal) * 100, 100), color, `${val}/${goal}${unit}`);
  }
  const fRow = softRow('Жиры', f, g.goalF, 'pf-purple', ' г');
  const cRow = softRow('Углеводы', c, g.goalC, 'pf-teal', ' г');

  return kRow + pRow + fRow + cRow;
}

export function wkStatusLine(w) {
  if (!w) return { icon: '', text: 'Нет записи' };
  if (w.rest) return { icon: cicon('moon', 14), text: 'Выходной' };
  const parts = [];
  if (w.cardio) parts.push('Кардио');
  if (w.strength) parts.push(w.strength);
  return { icon: cicon('dumbbell', 14), text: parts.length ? parts.join(' + ') : 'Тренировка' };
}

export function isRestDay(date) {
  const w = S.workoutLog.find(x => x.date === date);
  return !!(w && w.rest);
}

export function calcStreak() {
  let streak = 0;
  let d = new Date().toISOString().slice(0, 10);
  while (true) {
    const isActive = S.workoutLog.some(w => w.date === d && !w.rest && (w.cardio || w.strength));
    if (!isActive) break;
    streak++;
    d = shiftDate(d, -1);
    if (streak > 365) break;
  }
  return streak;
}

// Least-squares linear regression over the FULL weight history — every logged weigh-in
// pulls the trend line, not just whichever two/four happen to be most recent. Returns the
// slope in kg/day (negative = losing) anchored at day 0 = the first entry's date.
function fitWeightTrend(wlog) {
  const t0 = new Date(wlog[0].date).getTime();
  const xs = wlog.map(w => (new Date(w.date).getTime() - t0) / 86400000);
  const ys = wlog.map(w => w.w);
  const n = xs.length;
  const sumX = xs.reduce((a, b) => a + b, 0), sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((s, x, i) => s + x * ys[i], 0), sumXX = xs.reduce((s, x) => s + x * x, 0);
  const denom = n * sumXX - sumX * sumX;
  return denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
}

// Typical gap (days) between recent weigh-ins, used to project a date for the "next"
// forecast — based on actual recent logging cadence, not a hardcoded "every day"/"every week".
function avgLogGapDays(wlog) {
  const recent = wlog.slice(-8);
  let total = 0, n = 0;
  for (let i = 1; i < recent.length; i++) {
    const gap = (new Date(recent[i].date) - new Date(recent[i - 1].date)) / 86400000;
    if (gap > 0) { total += gap; n++; }
  }
  return n ? Math.max(1, Math.round(total / n)) : 1;
}

// ── Energy-balance model — uses logged food + workout calories alongside weight, not just
// weight alone (a pure weight-trend regression lags behind a real behavior change by however
// long it takes water weight / noise to average out). ──
const KCAL_PER_KG = 7700; // standard approximation: kcal of cumulative energy balance per kg of body mass change

function eatenKcalForDay(date) {
  let k = 0;
  ['breakfast', 'lunch', 'dinner', 'snacks'].forEach(m => {
    (S.foodLog[date]?.[m] || []).forEach(it => { if (it.eaten !== false) k += it.k || 0; });
  });
  return k;
}

function exerciseKcalForDay(date) {
  const w = S.workoutLog.find(x => x.date === date);
  return (w && w.kcalBurned) || 0;
}

function hasLoggedFood(date) {
  return ['breakfast', 'lunch', 'dinner', 'snacks'].some(m => (S.foodLog[date]?.[m] || []).some(it => it.eaten !== false));
}

// weightOnOrBefore, duplicated locally (pages/home.js can't be imported here — it already
// imports FROM this file, and a two-way import would be circular).
function weightOnOrBeforeLocal(dateStr) {
  const list = S.weightLog.filter(w => w.date <= dateStr).sort((a, b) => a.date.localeCompare(b.date));
  return list.length ? list[list.length - 1].w : S.startWeight;
}

// Personal daily maintenance calories (TDEE, excluding exercise), calibrated from real logged
// history: given everything actually eaten, everything actually burned via workouts, and the
// real weight change over that same span, solve for the one constant that reconciles them.
// This reflects this specific person's real metabolism and logging habits — far more accurate
// than a generic BMR formula. Only days with logged food count (an unlogged day is excluded,
// never assumed to be a zero-calorie day). Returns null when there isn't enough history to
// trust the result, or the result falls outside a physiologically sane range.
export function calibrateMaintenanceCalories() {
  const td = today();
  const loggedDays = Object.keys(S.foodLog).filter(d => d < td && hasLoggedFood(d)).sort();
  if (loggedDays.length < 10) return null;
  const first = loggedDays[0], last = loggedDays[loggedDays.length - 1];
  if ((new Date(last) - new Date(first)) / 86400000 < 10) return null;
  let totalEaten = 0, totalBurned = 0;
  loggedDays.forEach(d => { totalEaten += eatenKcalForDay(d); totalBurned += exerciseKcalForDay(d); });
  const wStart = weightOnOrBeforeLocal(first), wEnd = weightOnOrBeforeLocal(last);
  const observedChangeKg = wEnd - wStart;
  const tdee = (totalEaten - totalBurned - observedChangeKg * KCAL_PER_KG) / loggedDays.length;
  return (isFinite(tdee) && tdee >= 1000 && tdee <= 4500) ? tdee : null;
}

// Average daily intake/exercise-burn over the last `days` calendar days (excludes today,
// which is likely still incomplete) — reflects CURRENT behavior, which can differ from the
// full history used to calibrate maintenance calories above.
function recentDailyAverages(days) {
  const td = today();
  let loggedCount = 0, totalEaten = 0, totalBurned = 0;
  for (let d = shiftDate(td, -days); d < td; d = shiftDate(d, 1)) {
    if (hasLoggedFood(d)) { totalEaten += eatenKcalForDay(d); loggedCount++; }
    totalBurned += exerciseKcalForDay(d); // rest days genuinely burn 0 extra — average over ALL days, not just logged ones
  }
  return loggedCount < 5 ? null : { avgEaten: totalEaten / loggedCount, avgBurned: totalBurned / days };
}

// Energy-balance rate (kg/day) from recent real intake + exercise burn against the calibrated
// maintenance calories. Returns null when there isn't enough recent food-log data to trust it
// — the caller then falls back to the pure weight-trend rate.
function calorieBalanceRateKgPerDay() {
  const tdee = calibrateMaintenanceCalories();
  if (tdee == null) return null;
  const recent = recentDailyAverages(14);
  if (recent == null) return null;
  return (recent.avgEaten - tdee - recent.avgBurned) / KCAL_PER_KG;
}

// Blends the empirical weight-trend rate (the real observed outcome, including water weight
// and every other confound) with the energy-balance rate (reacts faster to a genuine recent
// change in eating/training, before it's fully visible on the scale). Weighted toward the
// empirical trend as the more authoritative signal; falls back to it alone when there isn't
// enough food-log history to calibrate a reliable energy-balance estimate.
function blendedRate(wlog) {
  const trendRate = fitWeightTrend(wlog);
  const calorieRate = calorieBalanceRateKgPerDay();
  return calorieRate == null ? trendRate : trendRate * 0.6 + calorieRate * 0.4;
}

export function calcProjection(wlog) {
  if (wlog.length < 2) return '—';
  const last = wlog[wlog.length - 1];
  // Goal already reached (or passed) — showing a projected date here would compute a
  // negative days-left and land on a date in the past, which read as "broken" rather
  // than as the achievement it actually is.
  if (last.w <= S.goalWeight) return 'Цель достигнута!';
  const rate = blendedRate(wlog); // kg/day
  if (rate >= -0.005) return 'Нет прогресса';
  const daysLeft = (last.w - S.goalWeight) / Math.abs(rate);
  const target = new Date(last.date + 'T12:00:00');
  target.setDate(target.getDate() + Math.round(daysLeft));
  const months = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  return target.getDate() + ' ' + months[target.getMonth()] + ' ' + target.getFullYear();
}

// Predicted weight at the next weigh-in, based on the same blended rate, anchored to the
// actual last logged value (not a regression's fitted value there, which can differ slightly
// from what was really recorded) and projected forward by the typical gap between recent
// entries.
export function calcNextWeighForecast(wlog) {
  if (wlog.length < 3) return null;
  const last = wlog[wlog.length - 1];
  const rate = blendedRate(wlog);
  if (Math.abs(rate) < 0.005) return null;
  const gap = avgLogGapDays(wlog);
  const predicted = n1(last.w + rate * gap);
  const target = new Date(last.date + 'T12:00:00');
  target.setDate(target.getDate() + gap);
  return { weight: predicted, date: fmtDateShort(target.toISOString().slice(0, 10)) };
}
